import type { Logger } from '../types';

import {
  TunnelResponse,
  TunnelResponseTopic,
  TunnelStartSessionResponse,
} from '@/common/interop/tunnel';
import { tunnel } from './iframe';

// pending id prefix - if pending id starts with this, we can reliably use window.name
const PENDING_ID_PREFIX = '_cfi_';

/**
 * Represents an interop session instance established via the tunnel.
 */
export class TunnelInstance {
  public id: string;
  public title = document.title;
  public pendingId: string | undefined;

  constructor(id: string, pendingId: string | undefined) {
    this.id = id;
    this.pendingId = pendingId;
  }
}

/**
 * The Tunnel class encapsulates an iframe that navigates to the Connectifi interop host's tunnel endpoint.
 * This endpoint plays a crucial role in validating and authenticating the current user, facilitating
 * the establishment of an interop session.
 *
 * Upon successful session establishment, users gain access to connect to the interop server's WebSocket endpoint.
 * Attempting to connect to this WebSocket endpoint without a previously established interop session through the
 * tunnel will result in a forbidden access response.
 */
export class Tunnel {
  private nextRefresh = 0;
  private refreshSessionTimer: number | undefined;

  constructor(
    private port: MessagePort,
    private logger: Logger,
    private appUrl: string,
    private appTitle: string,
  ) {}

  /**
   * An instance will be instantiated when an interop session is successfully established.
   */
  public instance: TunnelInstance | undefined;

  private createInstance(response: TunnelStartSessionResponse) {
    // prefer window.name if correct format, otherwise use the pendingId in the message
    const pendingId = window.name.startsWith(PENDING_ID_PREFIX)
      ? window.name.substring(5)
      : response.pendingId;
    this.instance = new TunnelInstance(response.instanceId, pendingId);
  }

  private clearRefreshSession() {
    clearInterval(this.refreshSessionTimer); // kill session refresh watcher
  }

  /**
   * An interop session is only valid for limited period of time defined by the server.
   * Once a session has expired the WebSocket is unusable and must be closed.
   * However, by calling the tunnel we can refresh and extend the length of our session.
   *
   * @param expiresMs the UTC time in milliseconds that the session expires.
   */
  private scheduleRefreshSession(refreshInMs: number | undefined) {
    if (!refreshInMs) return;
    this.logger.debug(`schedule session refresh in ${refreshInMs}ms`, {
      refreshInMs,
    });
    this.nextRefresh = new Date().getTime() + refreshInMs;
    this.refreshSessionTimer = window.setInterval(() => {
      const now = new Date().getTime();
      if (now > this.nextRefresh) {
        this.logger.debug(`calling tunnel to refresh session`);
        this.clearRefreshSession(); // tunnel reload will trigger scheduleRefreshSession
        this.touch();
      }
    }, 5000);
  }

  private handleTunnelResponse(response: TunnelResponse) {
    const isStartSession = response.topic === TunnelResponseTopic.StartSession;
    const isRefreshSession =
      response.topic === TunnelResponseTopic.RefreshSession;
    if (isStartSession) {
      this.createInstance(response);
    }
    if (isStartSession || isRefreshSession) {
      this.scheduleRefreshSession(response.refreshInMs);
    }
  }

  resetInstance() {
    // this should ensure a session/instanceId is created
    this.instance = undefined;
  }

  async touch() {
    const response = await tunnel(this.port, {
      instanceId: this.instance?.id,
      instanceTitle: this.appTitle,
      instanceUrl: this.appUrl,
    });
    this.handleTunnelResponse(response);
    return response;
  }
}
